/**
 * Safely stringifies an object to a pretty-printed JSON string.
 * - Handles circular references by replacing them with '[Circular Reference]'.
 * - Represents functions with a placeholder like '[Function: functionName]'.
 * - Returns a fallback error message if serialization fails for any other reason.
 *
 * @param obj The object to stringify.
 * @returns A formatted JSON string.
 */
export function safeJsonStringify(obj: unknown): string {
  const cache = new Set();

  const replacer = (key: string, value: unknown) => {
    // Gracefully handle functions
    if (typeof value === "function") {
      return `[Function: ${value.name || "anonymous"}]`;
    }

    // Detect and handle circular references
    if (typeof value === "object" && value !== null) {
      if (cache.has(value)) {
        // Circular reference found, discard key
        return "[Circular Reference]";
      }
      // Store value in our collection
      cache.add(value);
    }
    return value;
  };

  try {
    // The '2' argument enables pretty-printing for readability in logs
    return JSON.stringify(obj, replacer, 2);
  } catch (error) {
    return `[Failed to serialize object: ${error instanceof Error ? error.message : "Unknown error"}]`;
  }
}
